import type { FormApi } from '../../../form-hooks/useFormApi';
import type { NewMediaUploadFn } from '../../../hooks/api/useMediaUpload';
import type {
  ContentObject,
  ContentTypeDefinition,
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
  WithOptionalInternalProps,
} from '../../flotiq-client/types/api-response';

import { IFlotiqPluginEvent } from './IFlotiqPluginEvent';

export type FormRenderFieldEventInit = {
  /** Field name */
  name: string;
  /** Field value */
  value: any;
  /** Content type that includes the field */
  contentType?: WithOptionalInternalProps<ContentTypeDefinition> | null;
  /**
   * Initial data of the content object.
   * This will be either a new object or the object being edited.
   */
  initialData?: WithOptionalInternalProps<ContentObject> | null;
  /**
   * Unique key for form.
   * Use it to distinguish different forms rendered at the same time.
   * For example when user compares different versions of the object in the UI.
   */
  formUniqueKey?: string;
  /** Field properties */
  properties: ContentTypePropertyConfig;
  /** Field schema */
  schema: ContentTypePropertySchema;
  /** If content object is editing */
  isEditing?: boolean;
  /** If field is required */
  required: boolean;
  /** If field is disabled */
  disabled?: boolean;
  /** If field is read-only (non-interactive but without disabled styles) */
  readonly: boolean;
  /** An instance of form */
  form: FormApi;
  /** Field error returned by validation */
  error?: string[];
  /** Function to upload media files (mostly for media relations) */
  onMediaUpload?: NewMediaUploadFn;
};

/**
 * An event that is fired when a field is rendered in the object form.
 * Returning anything will embed that result in the form for the field instead of the original control.
 * This event is fired for every field in the form except hidden ones.
 *
 * @eventName "flotiq.form.field::render"
 * @memberof FlotiqPlugins.Events
 * @returns {null|string|array|number|boolean|HTMLElement|ReactElement}
 *          Null if rendering should be passed to either Flotiq or other plugins.
 *          Renderable result if plugin wants to replace the default renderer.
 */
export class FormRenderFieldEvent extends IFlotiqPluginEvent {
  constructor(initData: FormRenderFieldEventInit) {
    super(initData);
  }
}
